import * as Sentry from '@sentry/react';

const dsn = process.env.REACT_APP_SENTRY_DSN;
const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
const enabled = Boolean(dsn) && process.env.NODE_ENV === 'production' &&
  !['localhost', '127.0.0.1', '::1', 'dev.usenode.com'].includes(hostname);

export function canRecordSentryReplay(url) {
  try {
    const entry = new URL(url);
    // The recorder's initial URL is outside its custom-event filtering hook.
    // Keep replay off for invitation/authentication links carrying private data.
    return !entry.username && !entry.password && !entry.hash &&
      !Array.from(entry.searchParams.keys()).some((key) => /token|password|secret|email|auth|code/i.test(key));
  } catch (_) {
    return false;
  }
}

const recordReplay = enabled && canRecordSentryReplay(window.location.href);

export function sanitizeSentryUrl(value) {
  return typeof value === 'string'
    ? value.split(/[?#]/, 1)[0].replace(/^(https?:\/\/)[^/@]+@/i, '$1')
    : value;
}

// Keep diagnostics, but never copy request bodies, credentials or console payloads.
export function sanitizeSentryBreadcrumb(breadcrumb) {
  if (breadcrumb.category === 'console') return null;
  const data = breadcrumb.data;
  if (!data) return breadcrumb;
  const sanitized = { ...data };
  ['url', 'from', 'to', 'previous'].forEach((key) => {
    if (key in sanitized) sanitized[key] = sanitizeSentryUrl(sanitized[key]);
  });
  ['body', 'data', 'headers', 'cookies', 'request_body', 'response_body'].forEach((key) => {
    delete sanitized[key];
  });
  return { ...breadcrumb, data: sanitized };
}

export function sanitizeSentryEvent(event) {
  if (event.request) {
    // Sentry derives OS/browser/device from this SDK-provided header.
    const userAgent = Object.entries(event.request.headers || {})
      .find(([key]) => key.toLowerCase() === 'user-agent')?.[1];
    event.request = {
      url: sanitizeSentryUrl(event.request.url),
      method: event.request.method,
      ...(typeof userAgent === 'string' && userAgent ? { headers: { 'User-Agent': userAgent } } : {}),
    };
  }
  if (event.user) {
    const username = typeof event.user.username === 'string' ? event.user.username.trim() : '';
    event.user = event.user.id ? {
      id: event.user.id,
      ...(username ? { username } : {}),
    } : undefined;
  }
  if (event.urls) event.urls = event.urls.map(sanitizeSentryUrl);
  return event;
}

export function sanitizeSentryRecordingEvent(event) {
  const { tag, payload } = event.data;
  if (tag === 'breadcrumb') {
    const breadcrumb = sanitizeSentryBreadcrumb(payload);
    return breadcrumb ? { ...event, data: { ...event.data, payload: breadcrumb } } : null;
  }
  if (tag === 'performanceSpan' && /^(resource\.|navigation\.)/.test(payload.op)) {
    return {
      ...event,
      data: {
        ...event.data,
        payload: {
          ...payload,
          description: sanitizeSentryUrl(payload.description),
          data: sanitizeSentryBreadcrumb({ data: payload.data }).data,
        },
      },
    };
  }
  return event;
}

if (enabled) {
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    release: `node-web@${process.env.REACT_APP_VERSION || 'unknown'}`,
    sendDefaultPii: false,
    enableLogs: false,
    integrations: [
      Sentry.breadcrumbsIntegration({ console: false }),
      ...(recordReplay ? [Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        maskAttributes: ['title', 'placeholder', 'aria-label', 'href', 'src', 'srcset', 'action', 'formaction'],
        networkCaptureBodies: false,
        networkDetailAllowUrls: [],
        beforeAddRecordingEvent: sanitizeSentryRecordingEvent,
      })] : []),
    ],
    replaysSessionSampleRate: recordReplay ? 1.0 : 0,
    replaysOnErrorSampleRate: recordReplay ? 1.0 : 0,
    // Microsoft's email link scanner (Outlook Safe Links) rejects with this string while previewing shared links.
    ignoreErrors: [/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/],
    beforeBreadcrumb: sanitizeSentryBreadcrumb,
    beforeSend: sanitizeSentryEvent,
  });
  // Replay metadata passes through event processors rather than beforeSend.
  Sentry.addEventProcessor((event) => event.type === 'replay_event' ? sanitizeSentryEvent(event) : event);
  Sentry.setTag('surface', 'web');
}

// React 19 reports errors caught by the existing boundaries through these hooks.
export const sentryRootOptions = enabled ? {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
} : {};

export function setSentryUser(userId, businessId, { displayName, email, businessName, role } = {}) {
  if (!enabled) return;
  const username = (typeof displayName === 'string' ? displayName.trim() : '') ||
    (typeof email === 'string' ? email.trim() : '');
  const hasBusiness = Boolean(userId && businessId);
  Sentry.setUser(userId ? {
    id: String(userId),
    ...(username ? { username } : {}),
  } : null);
  Sentry.setTag('businessId', hasBusiness ? String(businessId) : undefined);
  Sentry.setTag('businessName', hasBusiness && typeof businessName === 'string' ? businessName.trim() || undefined : undefined);
  Sentry.setTag('role', hasBusiness && typeof role === 'string' ? role.trim() || undefined : undefined);
}

// The API wrapper resolves handled failures, so report terminal server errors explicitly.
// `code` is the backend intCode, so a grouped issue still tells which failure it was.
export function captureSentryApiFailure({ method, url, status, code }) {
  if (!enabled || !(status >= 500)) return;
  const endpoint = sanitizeSentryUrl(url);
  Sentry.captureException(new Error(`API ${method} ${endpoint} returned ${status}`), {
    fingerprint: ['api-server-error', String(method), endpoint, String(status)],
    tags: {
      api_method: method,
      api_endpoint: endpoint,
      http_status: status,
      ...(typeof code === 'string' && code ? { api_code: code.slice(0, 64) } : {}),
    },
  });
}
